/**
 * EcomNodes SaaS legal documents — metadata registry. Cycle 21 A (2026-06-08).
 *
 * The Turkish markdown bodies live in `./content/<slug>.md` (single source of
 * truth). The client renders them via react-markdown (see
 * client/src/features/legal); the contract PDF reads the same `.md` via fs.
 *
 * This registry is body-free so it can be imported anywhere (client + server +
 * tsx scripts). Adding a doc = a `.md` file + an entry here + a route.
 *
 * NOTE: distinct from `server/src/legal-templates/` (the per-market legal text
 * for the CUSTOMER's generated Shopify stores). These are EcomNodes' OWN docs.
 */
export const LEGAL_VERSION = 'v1-2026-06-08';

/**
 * Cycle 27 (2026-10-08) — the four sales documents were rewritten for one-time
 * credit packs (subscriptions grandfathered). Each doc's `version` tracks ITS
 * own `_Son güncelleme_` line; untouched docs stay on LEGAL_VERSION.
 */
export const LEGAL_VERSION_PACKS = 'v2-2026-10-08';

/**
 * 2026-10-18 — pack prices moved to their full price (47/87/117, no launch
 * discount): the two documents that carry the price table were re-issued.
 */
export const LEGAL_VERSION_PRICES = 'v3-2026-10-18';

export type LegalSlug =
  | 'mesafeli-satis-sozlesmesi'
  | 'on-bilgilendirme-formu'
  | 'kullanim-kosullari'
  | 'gizlilik-politikasi'
  | 'kvkk-aydinlatma'
  | 'kvkk-acik-riza'
  | 'cerez-politikasi'
  | 'iptal-iade-cayma'
  | 'ticari-elektronik-ileti';

export interface LegalDocMeta {
  slug: LegalSlug;
  /** Display title + H1 + <title>. */
  title: string;
  /** Content version stamp (matches the `_Son güncelleme_` line in the body). */
  version: string;
}

export const LEGAL_DOCS: LegalDocMeta[] = [
  { slug: 'mesafeli-satis-sozlesmesi', title: 'Mesafeli Satış Sözleşmesi', version: LEGAL_VERSION_PRICES },
  { slug: 'on-bilgilendirme-formu', title: 'Ön Bilgilendirme Formu', version: LEGAL_VERSION_PRICES },
  { slug: 'kullanim-kosullari', title: 'Kullanım Koşulları', version: LEGAL_VERSION_PACKS },
  { slug: 'gizlilik-politikasi', title: 'Gizlilik Politikası', version: LEGAL_VERSION },
  { slug: 'kvkk-aydinlatma', title: 'KVKK Aydınlatma Metni', version: LEGAL_VERSION },
  { slug: 'kvkk-acik-riza', title: 'KVKK Açık Rıza Metni', version: LEGAL_VERSION },
  { slug: 'cerez-politikasi', title: 'Çerez Politikası', version: LEGAL_VERSION },
  { slug: 'iptal-iade-cayma', title: 'İptal, İade ve Cayma Politikası', version: LEGAL_VERSION_PACKS },
  { slug: 'ticari-elektronik-ileti', title: 'Ticari Elektronik İleti Aydınlatması', version: LEGAL_VERSION },
];

export const LEGAL_DOCS_BY_SLUG: Record<LegalSlug, LegalDocMeta> = Object.fromEntries(
  LEGAL_DOCS.map((d) => [d.slug, d]),
) as Record<LegalSlug, LegalDocMeta>;
